import type { Category, Localized } from '../types'

type DraftFaq = { question: string; answer: string }

export interface PocketPatchParadeDraftContent {
  id: string
  category: Category
  icon: string
  colors: [string, string]
  title: Localized
  description: Localized
  desktop: Localized
  mobile: Localized
  rules: Record<'en' | 'zh', string[]>
  tips: Record<'en' | 'zh', string[]>
  faq: Record<'en' | 'zh', DraftFaq[]>
  editorial: Localized
  testing: Localized
}

/**
 * Reviewed content shared by the game registry, the canonical game page and
 * the editorial record. Keeping one source prevents the bilingual copy from
 * drifting away from the implemented four-level experience.
 */
export const pocketPatchParadeDraftContent: PocketPatchParadeDraftContent = {
  id: 'pocket-patch-parade',
  category: 'casual',
  icon: '🧰',
  colors: ['#ffb45c', '#2f7f78'],
  title: { en: 'Pocket Patch Parade', zh: '口袋修补巡游' },
  description: {
    en: 'Restore four small treasures through connected, bite-size repair levels that mix observation, sequence, matching and timing.',
    zh: '通过四个相连的短关卡修好旧物，在观察、顺序、匹配与时机挑战中完成整场巡游。',
  },
  desktop: {
    en: 'Click repair tools or use number keys; arrows adjust values, Enter checks, P pauses and R restarts.',
    zh: '点击工具或使用数字键；方向键调节数值，Enter 确认，P 暂停，R 重开。',
  },
  mobile: {
    en: 'Tap the large repair controls, sliders and timing button. Every essential action is available on screen.',
    zh: '轻触大尺寸修补按钮、滑杆与时机键；所有必要操作都可在屏幕上完成。',
  },
  rules: {
    en: [
      'Complete Morning Lamp, Paper Kite, Pocket Radio and Parade Chime in order. Finishing one repair unlocks the next on the same route.',
      'Each level contains three connected steps with its own visible target and a learnable failure condition; pausing freezes the timer and tools.',
      'Accuracy, unused hints and finishing near the target time award up to three stars. A better star result or faster clear replaces the local record.',
      'Progress and best times remain only in this browser. Clear progress requires confirmation and returns the route to level one.',
    ],
    zh: [
      '按顺序完成晨灯复明、纸鸢归线、口袋收音机和巡游风铃；修好一件物品后，下一关会在同一路线上解锁。',
      '每关包含三个相连步骤，拥有可见目标与可学会的失败条件；暂停时计时与工具会一同冻结。',
      '准确操作、保留提示与在目标时间内完成可获得最多三星；更高星级或更快成绩会替换本地记录。',
      '解锁、星级与最快时间只保存在当前浏览器；确认清档后，路线会回到第一关。',
    ],
  },
  tips: {
    en: [
      'Read the printed target before touching a tool. Every correct solution remains visible, so observation is faster than trial and error.',
      'Use a hint only after identifying the current step: hints cost quality, but they never change the deterministic solution you are learning.',
      'For the radio and chime, rehearse the visible number sequence once before entering it; a wrong note resets only that short input, not the whole route.',
      'On the final resonance meter, wait for the marker to enter the highlighted center band instead of chasing it with repeated taps.',
    ],
    zh: [
      '操作前先读印在物品或卡片上的目标；正确答案一直可见，观察比反复试错更快。',
      '先确定当前步骤再使用提示；提示会影响质量星级，但不会改变正在学习的固定解法。',
      '在收音机与风铃关中，先在心里过一遍可见数字序列再输入；错音只会重置这段短序列。',
      '最后的共鸣仪表中，等待游标进入高亮的中心区再敲击，不要连续乱点。',
    ],
  },
  faq: {
    en: [
      { question: 'Are all four levels real and playable?', answer: 'Yes. Each level has three implemented steps, a complete success path and a repeatable failure path. There are no empty level cards.' },
      { question: 'Can I continue on another device?', answer: 'No. Unlocks, stars and best times stay in this browser and are not tied to an account or uploaded.' },
      { question: 'Is the game playable without sound?', answer: 'Yes. Every sound cue is paired with visible status text and animation, and sound can be muted at any time.' },
      { question: 'Does restarting erase the whole route?', answer: 'No. Restart resets only the current level. The separate Clear progress control resets the route after confirmation.' },
    ],
    zh: [
      { question: '四个关卡都是真实可玩的吗？', answer: '是。每关都有三个已实现步骤、完整成功路径与可重复的失败路径，不存在空关卡。' },
      { question: '可以换一台设备继续吗？', answer: '不可以。解锁、星级与最快时间只保存在当前浏览器，不绑定账号，也不会上传。' },
      { question: '关掉声音后还能正常玩吗？', answer: '可以。每个声音反馈都有对应的状态文字与动画，声音也可随时关闭。' },
      { question: '重开会清掉整条路线吗？', answer: '不会。重开只重置当前关；只有单独的“清除进度”并经过确认后才会重置整条路线。' },
    ],
  },
  editorial: {
    en: 'An original connected repair journey: four objects share one progression system, while each level changes the reasoning and input instead of repainting the same task.',
    zh: '这是一次原创的连续修补旅程：四件物品共用同一进度系统，但每关都会改变判断与输入，而不是为同一任务换色。',
  },
  testing: {
    en: 'Logic tests cover all four win and loss paths, progress normalization and best records; browser QA covers sequential unlocks, persistence, reset, keyboard, touch, sharing, analytics and responsive layout.',
    zh: '逻辑测试覆盖四关成败路径、进度标准化与最佳记录；浏览器验证覆盖顺序解锁、持久化、清档、键盘、触控、分享、统计与响应式布局。',
  },
}
